import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOnlineStatus } from '../../offline/ConnectivityContext';
import { getCachedMeta, setCachedMeta } from '../../offline/localStore';

/* ─────────────────────────────────────────────
   Fetch/cache hook for the usage_taxonomy collection (categories +
   subcategories classifying usages.type_usage). The whole tree is small
   (well under 200 docs) so callers always get everything in one request;
   `categories`/`subcategoriesByCategory` are the active-only, sorted views
   needed by pickers, while `taxonomy` (raw, includes inactive nodes) is
   what the admin screen needs.

   `buildingId` (optional): when provided, the backend also returns
   subcategories scoped to that one building (`scope: "building"`, created ad
   hoc during an audit to avoid polluting the shared referential) alongside
   the global ones. Omit it (e.g. the admin screen) to only ever see the
   global, curated referential.

   Cached in IndexedDB (table `meta`, offline/db.js) on every successful
   fetch, and read back from that cache when offline or when the fetch fails
   — same rationale/pattern as useAppMetadata.
───────────────────────────────────────────── */
export function useUsageTaxonomy(buildingId = null) {
    const [taxonomy, setTaxonomy] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isOnline } = useOnlineStatus();
    const cacheKey = `usage_taxonomy:${buildingId || 'global'}`;

    const reload = useCallback(async () => {
        setLoading(true);
        if (!isOnline) {
            const cached = await getCachedMeta(cacheKey);
            setTaxonomy(Array.isArray(cached) ? cached : []);
            setLoading(false);
            return;
        }
        const url = buildingId
            ? `${process.env.REACT_APP_API_URL}/get_usage_taxonomy?building_id=${encodeURIComponent(buildingId)}`
            : `${process.env.REACT_APP_API_URL}/get_usage_taxonomy`;
        try {
            const r = await fetch(url);
            const data = await r.json();
            const list = Array.isArray(data) ? data : [];
            setTaxonomy(list);
            setCachedMeta(cacheKey, list).catch(() => {});
        } catch {
            const cached = await getCachedMeta(cacheKey);
            setTaxonomy(Array.isArray(cached) ? cached : []);
        } finally {
            setLoading(false);
        }
    }, [buildingId, cacheKey, isOnline]);

    useEffect(() => { reload(); }, [reload]);

    const categories = useMemo(() => (
        taxonomy
            .filter((node) => node.type === 'category' && node.active)
            .sort((a, b) => a.order - b.order)
    ), [taxonomy]);

    const subcategoriesByCategory = useMemo(() => {
        const map = {};
        taxonomy
            .filter((node) => node.type === 'subcategory' && node.active)
            .sort((a, b) => a.order - b.order)
            .forEach((sub) => {
                if (!map[sub.parent_id]) map[sub.parent_id] = [];
                map[sub.parent_id].push(sub);
            });
        return map;
    }, [taxonomy]);

    return { taxonomy, categories, subcategoriesByCategory, loading, reload };
}
